import { type ApiEnvelope, api, unwrap } from "@/lib/api";

import type {
	BankStatementDto,
	CodEntryDto,
	DisputeDto,
	DisputeStatus,
	MomoLiveResponse,
	PaymentDetailResponse,
	PaymentListResponse,
	PaymentSearchParams,
	ProviderDto,
	ReconciliationEntryDto,
	RefundDto,
} from "./types";

function toParams(p: PaymentSearchParams): URLSearchParams {
	const usp = new URLSearchParams();
	if (p.query) usp.append("query", p.query);
	p.status?.forEach((s) => usp.append("status", s));
	p.method?.forEach((m) => usp.append("method", m));
	if (p.providerCode) usp.append("providerCode", p.providerCode);
	if (p.from) usp.append("from", p.from);
	if (p.to) usp.append("to", p.to);
	if (p.amountMin !== undefined) usp.append("amountMin", String(p.amountMin));
	if (p.amountMax !== undefined) usp.append("amountMax", String(p.amountMax));
	if (p.orderId) usp.append("orderId", p.orderId);
	if (p.customerId) usp.append("customerId", p.customerId);
	if (p.page) usp.append("page", String(p.page));
	if (p.pageSize) usp.append("pageSize", String(p.pageSize));
	return usp;
}

export const paymentsApi = {
	list: (params: PaymentSearchParams = {}) =>
		unwrap<PaymentListResponse>(
			api.get<ApiEnvelope<PaymentListResponse>>(
				`/admin/payments?${toParams(params).toString()}`,
			),
		),
	detail: (id: string) =>
		unwrap<PaymentDetailResponse>(
			api.get<ApiEnvelope<PaymentDetailResponse>>(`/admin/payments/${id}`),
		),
	live: () =>
		unwrap<MomoLiveResponse>(
			api.get<ApiEnvelope<MomoLiveResponse>>(`/admin/payments/momo-live`),
		),
	forceCheck: (id: string) =>
		unwrap<PaymentDetailResponse>(
			api.post<ApiEnvelope<PaymentDetailResponse>>(
				`/admin/payments/${id}/force-check`,
			),
		),
	create: (payload: {
		orderId?: string;
		customerId?: string;
		method: string;
		amountGross: number;
		providerCode?: string;
		payerMsisdn?: string;
		idempotencyKey?: string;
	}) => unwrap(api.post<ApiEnvelope<unknown>>(`/admin/payments`, payload)),
	refunds: {
		create: (
			paymentId: string,
			payload: {
				amount: number;
				reasonCode: string;
				reasonText?: string;
				idempotencyKey: string;
			},
		) =>
			unwrap<RefundDto>(
				api.post<ApiEnvelope<RefundDto>>(
					`/admin/payments/${paymentId}/refunds`,
					payload,
				),
			),
		approve: (refundId: string) =>
			unwrap<RefundDto>(
				api.post<ApiEnvelope<RefundDto>>(
					`/admin/payments/refunds/${refundId}/approve`,
				),
			),
		list: (status?: string) =>
			unwrap<RefundDto[]>(
				api.get<ApiEnvelope<RefundDto[]>>(
					`/admin/payments/refunds/list${status ? `?status=${status}` : ""}`,
				),
			),
	},
	cod: {
		list: () =>
			unwrap<CodEntryDto[]>(
				api.get<ApiEnvelope<CodEntryDto[]>>(`/admin/payments/cod-entries`),
			),
		create: (payload: {
			orderId: string;
			expectedAmount: number;
			collectedAmount: number;
			hubId?: string;
			notes?: string;
		}) =>
			unwrap<CodEntryDto>(
				api.post<ApiEnvelope<CodEntryDto>>(
					`/admin/payments/cod-entries`,
					payload,
				),
			),
	},
	reconciliation: {
		statements: () =>
			unwrap<BankStatementDto[]>(
				api.get<ApiEnvelope<BankStatementDto[]>>(
					`/admin/payments/bank-statements`,
				),
			),
		importStatement: (payload: {
			source: string;
			periodFrom: string;
			periodTo: string;
			fileContent: string;
			fileName?: string;
		}) =>
			unwrap<BankStatementDto>(
				api.post<ApiEnvelope<BankStatementDto>>(
					`/admin/payments/bank-statements/import`,
					payload,
				),
			),
		entries: (statementId?: string) =>
			unwrap<ReconciliationEntryDto[]>(
				api.get<ApiEnvelope<ReconciliationEntryDto[]>>(
					`/admin/payments/reconciliation${statementId ? `?statementId=${statementId}` : ""}`,
				),
			),
		match: (payload: {
			entryId: string;
			paymentId: string;
			manualReason: string;
		}) =>
			unwrap(
				api.post<ApiEnvelope<unknown>>(
					`/admin/payments/reconciliation/match`,
					payload,
				),
			),
		close: (statementId: string) =>
			unwrap(
				api.post<ApiEnvelope<unknown>>(
					`/admin/payments/reconciliation/close-period`,
					{
						statementId,
					},
				),
			),
		unlock: (statementId: string, reason: string) =>
			unwrap(
				api.post<ApiEnvelope<unknown>>(
					`/admin/payments/reconciliation/unlock-period`,
					{
						statementId,
						reason,
					},
				),
			),
	},
	disputes: {
		list: (status?: DisputeStatus) =>
			unwrap<DisputeDto[]>(
				api.get<ApiEnvelope<DisputeDto[]>>(
					`/admin/payments/disputes${status ? `?status=${status}` : ""}`,
				),
			),
		update: (id: string, payload: { status?: string; resolution?: string }) =>
			unwrap<DisputeDto>(
				api.patch<ApiEnvelope<DisputeDto>>(
					`/admin/payments/disputes/${id}`,
					payload,
				),
			),
		open: (payload: {
			paymentId: string;
			kind: string;
			amountDisputed: number;
		}) =>
			unwrap<DisputeDto>(
				api.post<ApiEnvelope<DisputeDto>>(`/admin/payments/disputes`, payload),
			),
	},
	providers: {
		list: () =>
			unwrap<ProviderDto[]>(
				api.get<ApiEnvelope<ProviderDto[]>>(`/admin/payments/providers`),
			),
		update: (
			code: string,
			payload: { label?: string; isActive?: boolean; isPrimary?: boolean },
		) =>
			unwrap(
				api.patch<ApiEnvelope<unknown>>(
					`/admin/payments/providers/${code}`,
					payload,
				),
			),
		test: (code: string) =>
			unwrap<{ code: string; operational: boolean }>(
				api.post<ApiEnvelope<{ code: string; operational: boolean }>>(
					`/admin/payments/providers/${code}/test`,
				),
			),
	},
	exportCsvUrl: (params: PaymentSearchParams = {}) =>
		`/api/v1/admin/payments/export?${toParams(params).toString()}`,
};
